import { useMemo } from "react";

import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { PropertyDefinitionInputTypeEnum } from "src/clients/features-control/api";
import { EventConfigurationRoot } from "src/eventsCatalogue/EventConfigurationRoot";
import {
  useEventSchema,
  useUpdateEventType,
} from "src/eventsCatalogue/queries";
import { PropertiesSchemaEditor } from "src/eventsCatalogue/SchemaEditor/PropertiesSchemaEditor";
import { EventPageParams, getUrlToEventsPage } from "src/router/urls";
import { SchemaEditorSidebar } from "src/schema/schemaEditor/SchemaEditorSidebar";
import { EventSchemaFormData } from "src/schema/schemaEditor/types";
import { useSchemaForm } from "src/schema/schemaEditor/useSchemaForm";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const EventSchemaEditorSidebar = () => {
  const {
    wsId,
    orgId,
    event_type: eventType,
  } = useParamsDecode<EventPageParams>();
  const navigate = useNavigate();
  const { data: eventTypeConfig, isLoading } = useEventSchema(eventType);

  const { mutateAsync: updateEventType } = useUpdateEventType();

  const eventConfig = useMemo<EventSchemaFormData | undefined>(
    () =>
      eventTypeConfig
        ? {
            etag: eventTypeConfig.etag ?? "",
            created_at: eventTypeConfig.created_at ?? "",
            updated_at: eventTypeConfig.updated_at ?? "",
            event_type: eventTypeConfig.event_type ?? "",
            internal_name: eventTypeConfig.internal_name ?? "",
            display_name_singular: eventTypeConfig.display_name_singular ?? "",
            display_name_plural: eventTypeConfig.display_name_plural ?? "",
            display_symbol: eventTypeConfig.display_symbol ?? "",
            properties: eventTypeConfig.properties,
            entities_json_schema: eventTypeConfig.entities_json_schema ?? {},
            json_schema: eventTypeConfig.json_schema ?? {},
          }
        : undefined,
    [eventTypeConfig],
  );

  const { form, handleSubmit, isSaving, canSave } = useSchemaForm(
    eventConfig,
    async (data) => {
      if (!data.etag) return data;
      const propertiesInput = Object.fromEntries(
        Object.entries(data.properties).map(([key, prop]) => {
          const { type, required, _sensitive, ...rest } = prop;
          return [
            key,
            {
              type: type as unknown as PropertyDefinitionInputTypeEnum,
              required,
              sensitive: _sensitive ?? false,
              ...rest,
            },
          ];
        }),
      );
      const result = await updateEventType({
        etag: data.etag,
        eventType: data.event_type,
        ...data,
        properties: propertiesInput,
      });
      return {
        ...result,
        properties: result.properties,
      } as EventSchemaFormData;
    },
  );

  const onSubmit = form.handleSubmit(handleSubmit);

  return (
    <form onSubmit={onSubmit}>
      <FormProvider {...form}>
        <SchemaEditorSidebar
          canSave={canSave}
          isSaving={isSaving}
          schema={eventConfig}
          title="Edit Event schema"
          tooltipText="For additional changes you want to make on Event Schema, please contact Taktile team."
          onClose={() =>
            navigate(
              getUrlToEventsPage({
                orgId,
                wsId,
                eventType,
              }),
            )
          }
        >
          <EventConfigurationRoot isLoading={isLoading} />
          <PropertiesSchemaEditor isLoading={isLoading} />
        </SchemaEditorSidebar>
      </FormProvider>
    </form>
  );
};
